/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useState, type FormEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import { monoLabel, inputStyle } from '../../lib/ui.js';
import { trpc } from '../../lib/trpc.js';
import { ReviewHistory, type PastReview } from './ReviewHistory.js';

const p = appPalette;

export function Review({ weekEnd }: { weekEnd: string }) {
  const utils = trpc.useUtils();
  const run = trpc.review.run.useMutation({
    onSuccess: async () => {
      await utils.review.history.invalidate();
    },
  });
  const history = trpc.review.history.useQuery();
  const [reflection, setReflection] = useState('');
  const [nextWeekFocus, setNextWeekFocus] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (reflection.trim() === '') return;
    run.mutate({
      weekEnd,
      reflection: reflection.trim(),
      nextWeekFocus: nextWeekFocus.trim() === '' ? null : nextWeekFocus.trim(),
    });
  };

  const result = run.data;

  const pastReviews: PastReview[] = (history.data ?? []).map((r) => ({
    id: r.id,
    periodEnd: r.weekEnd,
    closingObservation: r.closingObservation,
    reviewMarkdown: r.reviewMarkdown,
    priorities: r.nextWeekPriorities,
  }));

  return (
    <>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ ...monoLabel }}>Weekly review</div>

        {result ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="serif" style={{ fontSize: 16, lineHeight: 1.55, color: p.fg }}>
              <ReactMarkdown>{result.reviewMarkdown}</ReactMarkdown>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ ...monoLabel }}>Next week</span>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                {result.nextWeekPriorities.map((pr, i) => (
                  <li
                    key={i}
                    style={{
                      fontFamily: 'var(--font-serif)',
                      fontFeatureSettings: 'var(--serif-feat)',
                      fontSize: 15,
                      color: p.fg,
                      marginBottom: 4,
                    }}
                  >
                    {pr}
                  </li>
                ))}
              </ol>
            </div>
            <p
              style={{
                fontFamily: 'var(--font-serif)',
                fontFeatureSettings: 'var(--serif-feat)',
                fontStyle: 'italic',
                fontSize: 15,
                color: p.muted,
                margin: 0,
              }}
            >
              {result.closingObservation}
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="How did the week go? Wins, and what slipped."
              aria-label="How did the week go?"
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
            />
            <textarea
              value={nextWeekFocus}
              onChange={(e) => setNextWeekFocus(e.target.value)}
              placeholder="What would make next week a success? (optional)"
              aria-label="What would make next week a success?"
              rows={2}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 56 }}
            />
            <button
              type="submit"
              disabled={run.isPending || reflection.trim() === ''}
              style={{
                alignSelf: 'flex-start',
                padding: '10px 18px',
                background: p.fg,
                color: p.bg,
                border: 'none',
                fontSize: 13,
                cursor: run.isPending || reflection.trim() === '' ? 'not-allowed' : 'pointer',
                opacity: run.isPending || reflection.trim() === '' ? 0.6 : 1,
              }}
            >
              {run.isPending ? 'Reviewing…' : 'Run review →'}
            </button>
          </form>
        )}
        {run.error ? (
          <span style={{ ...monoLabel, color: p.accent }}>{run.error.message}</span>
        ) : null}
      </section>

      <ReviewHistory
        label="Past reviews"
        prioritiesLabel="Next week"
        reviews={pastReviews}
        isPending={history.isPending}
      />
    </>
  );
}
